<template>
  <HgLayout
    id="app"
    :class="{ hideHeaderFooter: isIFrameView }"
    :baseUrl="envBaseUrl"
    :currentLanguage="locale"
    :currentRoute="route"
    :hideMainNavigation="hideMainNavigation"
    :getLanguageToLink="(language: HgSupportedLanguage) => getLanguageSwitchLink(route, language)"
    :isNavigationExperimentEnabled="isNavigationExperimentEnabled"
    @trackFooterClick="trackClickHandler"
  >
    <template #headerMetaAction>
      <HgMainNavItem
        :menuItem="tm('$hg.nav.harmonize.newListingMenuItem')"
        :currentRoute="route"
        :baseUrl="envBaseUrl"
        :bordered="true"
      />
    </template>
    <template #headerMetaNavigation>
      <FavouritesLink
        :baseUrl="envBaseUrl"
        :route="route"
        :menuItem="tm('$hg.nav.favoriteMenuItem')"
        :menuNavItem="HgMainNavItem"
        @mainNavItemClick="handleMainNavClick"
      />
      <HgMainNavItem
        :menuItem="userMenuItem"
        :accountsOrigin="envBaseUrl"
        :icon="Icon.PersonOutline"
        :currentRoute="route"
        :baseUrl="envBaseUrl"
        dropdownOrientation="right"
        @mainNavItemClick="handleMainNavClick"
        @dropDownMenuItemClick="handleMainNavClick"
      />
    </template>
    <template #sideMenuMetaNavigation>
      <FavouritesLink
        :baseUrl="envBaseUrl"
        :route="route"
        :menuItem="tm('$hg.nav.favoriteMenuItem')"
        :menuNavItem="HgSideMenuNavItem"
        @sideMenuItemClick="handleMobileMenuNavClick"
      />
      <HgSideMenuNavItem
        v-if="isLoggedIn"
        :menuItem="tm('$hg.nav.harmonize.overviewMenuItem')"
        :icon="Icon.PersonOutline"
        :currentRoute="route"
        :baseUrl="envBaseUrl"
        @sideMenuItemClick="handleMobileMenuNavClick"
      />
      <HgSideMenuNavItem
        v-if="isLoggedIn"
        :menuItem="tm('$hg.nav.harmonize.settingsMenuItem')"
        :icon="Icon.Settings"
        :currentRoute="route"
        :baseUrl="envBaseUrl"
        @sideMenuItemClick="handleMobileMenuNavClick"
      />
      <HgSideMenuNavItem
        v-if="isLoggedIn"
        :menuItem="tm('$hg.nav.harmonize.myAdsMenuItem')"
        :icon="Icon.Appointment"
        :currentRoute="route"
        :baseUrl="envBaseUrl"
        @sideMenuItemClick="handleMobileMenuNavClick"
      />
      <HgSideMenuNavItem
        v-if="isLoggedIn"
        :menuItem="tm('$hg.nav.harmonize.searchAlertMenuItem')"
        :icon="Icon.Bell"
        :currentRoute="route"
        :baseUrl="envBaseUrl"
        @sideMenuItemClick="handleMobileMenuNavClick"
      />
      <HgSideMenuNavItem
        v-if="isLoggedIn && !shouldHideMyTenantPlusMenuItem"
        :menuItem="tm('$hg.nav.harmonize.tenantPlusMenuItem')"
        :icon="Icon.PlusMembership"
        :currentRoute="route"
        :baseUrl="envBaseUrl"
        @sideMenuItemClick="handleMobileMenuNavClick"
      />
      <HgSideMenuNavItem
        v-if="!isLoggedIn"
        :menuItem="tm('$hg.nav.harmonize.signInMenuItem')"
        :icon="Icon.PersonOutline"
        :currentRoute="route"
        :baseUrl="envBaseUrl"
        @sideMenuItemClick="handleMobileMenuNavClick"
      />
      <HgSideMenuNavItem
        v-else
        :menuItem="tm('$hg.nav.harmonize.signOutMenuItem')"
        :icon="Icon.SignOut"
        :currentRoute="route"
        :baseUrl="envBaseUrl"
        @click="handleSignOut"
        @sideMenuItemClick="handleMobileMenuNavClick"
      />
    </template>

    <div :class="{ [$style.main]: !isLandingPage }">
      <router-view />
    </div>
  </HgLayout>
</template>

<script setup lang="ts">
import {
  HgLayout,
  HgMainNavItem,
  HgSideMenuNavItem,
  HgSupportedLanguage,
  Icon,
  type MenuItem,
} from '@smg-real-estate/domus';
import { FavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { useAndroidWebViewParams } from '@/composables/useAndroidWebViewParams';
import { usePreloadErrorHandler } from '@/composables/usePreloadErrorHandler';
import { getLanguageSwitchLink } from '@/helpers/listingUtils';
import { trackClickHandler } from '@/helpers/trackingHelper';
import { useFeatureFlagsStore } from '@/stores/featureFlags';
import { FeatureFlagName } from '@/types';

import { useApp } from './useApp';

const route = useRoute();
const { locale, t, tm } = useI18n();

usePreloadErrorHandler();

const {
  envBaseUrl,
  handleMainNavClick,
  handleMobileMenuNavClick,
  handleSignOut,
  isLoggedIn,
  hideMainNavigation,
} = useApp();
const { shouldHideMyTenantPlusMenuItem } = useAndroidWebViewParams();
const featureFlagsStore = useFeatureFlagsStore();

const isIFrameView = computed(() => route.name === 'landingpage-iframe');
const isLandingPage = computed(
  () => String(route.name).startsWith('landingpage-') && route.name !== 'landingpage-iframe',
);
const isNavigationExperimentEnabled = computed(() =>
  featureFlagsStore.isTreatmentOn(FeatureFlagName.DD4035_NAVIGATION_EXPERIMENT),
);

const signInMenuItem = computed<MenuItem>(() => tm('$hg.nav.harmonize.signInMenuItem'));
const dropdownMenuItem = computed(() => ({
  label: t('$hg.nav.loggedInMenuItem.label'),
  children: tm('$hg.nav.privateItems') as MenuItem[],
}));
const userMenuItem = computed(() =>
  isLoggedIn.value ? dropdownMenuItem.value : signInMenuItem.value,
);
</script>

<style lang="scss">
@import '@/assets/global-hg';

.hideHeaderFooter {
  header {
    display: none;
  }

  footer {
    display: none;
  }
}
</style>

<style lang="scss" module>
@import '@/assets/variables';

.main {
  margin-bottom: $space-600;
}
</style>
